/**
 * Safe localStorage/sessionStorage utility with validation
 * Prevents runtime errors from corrupted or malformed JSON data
 *
 * @see React Best Practices: client-localstorage-schema
 */

type StorageType = 'local' | 'session';

interface StorageOptions<T> {
  /** Default value if key doesn't exist or parsing fails */
  defaultValue: T;
  /** Optional validator function to verify data structure */
  validate?: (data: unknown) => data is T;
}

/**
 * Safely get and parse JSON from localStorage/sessionStorage
 * Returns defaultValue if key doesn't exist, parsing fails, or validation fails
 */
export function safeGetJSON<T>(
  key: string,
  options: StorageOptions<T>,
  storage: StorageType = 'local'
): T {
  if (typeof window === 'undefined') {
    return options.defaultValue;
  }

  try {
    const storageObj = storage === 'local' ? localStorage : sessionStorage;
    const item = storageObj.getItem(key);

    if (item === null) {
      return options.defaultValue;
    }

    const parsed = JSON.parse(item);

    // If validator provided, use it
    if (options.validate && !options.validate(parsed)) {
      console.warn(`[safeStorage] Validation failed for key "${key}", returning default`);
      return options.defaultValue;
    }

    return parsed as T;
  } catch (error) {
    console.warn(`[safeStorage] Failed to parse key "${key}":`, error);
    return options.defaultValue;
  }
}

/**
 * Safely set JSON in localStorage/sessionStorage
 */
export function safeSetJSON<T>(
  key: string,
  value: T,
  storage: StorageType = 'local'
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const storageObj = storage === 'local' ? localStorage : sessionStorage;
    storageObj.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`[safeStorage] Failed to set key "${key}":`, error);
    return false;
  }
}

/**
 * Safely remove item from localStorage/sessionStorage
 */
export function safeRemove(key: string, storage: StorageType = 'local'): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const storageObj = storage === 'local' ? localStorage : sessionStorage;
    storageObj.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[safeStorage] Failed to remove key "${key}":`, error);
    return false;
  }
}

/**
 * Clear multiple keys at once (useful for session cleanup)
 */
export function safeClearKeys(keys: string[], storage: StorageType = 'local'): void {
  if (typeof window === 'undefined') {
    return;
  }

  const storageObj = storage === 'local' ? localStorage : sessionStorage;
  keys.forEach(key => {
    try {
      storageObj.removeItem(key);
    } catch (error) {
      console.warn(`[safeStorage] Failed to remove key "${key}":`, error);
    }
  });
}

// Common session keys used throughout the app
export const SESSION_KEYS = {
  sessionActive: 'insuracentral_session_was_active',
  sessionHeartbeat: 'insuracentral_session_heartbeat',
  loggedOut: 'insuracentral_logged_out',
  sessionEnding: 'insuracentral_session_ending',
  tabClosing: 'insuracentral_tab_closing',
} as const;

/**
 * Clear all InsuraCentral session-related localStorage keys
 * Use this during logout to ensure clean state
 */
export function clearSessionStorage(): void {
  safeClearKeys(Object.values(SESSION_KEYS));
}

// Type validators for common data structures
export const validators = {
  isArray: (data: unknown): data is unknown[] => Array.isArray(data),
  isObject: (data: unknown): data is Record<string, unknown> =>
    typeof data === 'object' && data !== null && !Array.isArray(data),
  isString: (data: unknown): data is string => typeof data === 'string',
  isNumber: (data: unknown): data is number => typeof data === 'number',
  isBoolean: (data: unknown): data is boolean => typeof data === 'boolean',
};
